import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogContent, DialogTitle, IconButton,
} from '@material-ui/core';
import { Clear } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import api from '../../services/api';
import { isAddressValid } from '../../services/helpers';

const SetZonePriceTable = ({
  row, companies, onClose, open, update,
}) => {
  const [errors, setErrors] = useState({});
  const [record, setRecord] = useState({});
  const [loading, setLoading] = useState(false);
  const [carrierServices, setCarrierServices] = useState([]);
  const [carriers, setCarriers] = useState([]);

  const isEdit = !isEmpty(row);

  useEffect(() => {
    if (isEdit) setRecord(row);
    else {
      setRecord({
        company_id: '',
        carrier_id: '',
        carrier_service_id: '',
        zone: '',
        weight: '',
        price: '',
      });
    }
  }, [isEdit, row]);

  useEffect(() => {
    api.zonePriceTable.getCarriers({ company: record.company_id }).then((data) => {
      setCarriers(data.carriers);
      setCarrierServices([]);
    });
  }, [record.company_id]);

  useEffect(() => {
    api.zonePriceTable.getCarrierServices({ carrier: record.carrier_id }).then(({ services }) => {
      setCarrierServices(services);
    });
  }, [record.carrier_id]);

  const validate = useCallback(() => {
    Object.keys(record).forEach((key) => {
      if (!record[key]) {
        errors[key] = `Please enter ${key.replace('_', ' ')}`;
      }
    });
    setErrors({ ...errors });
    return errors;
  }, [record]);

  const handleChange = (e) => {
    const { id: field, value } = e.target;

    setRecord({
      ...record,
      [field]: value,
    });
    setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = () => {
    const method = isEdit ? 'update' : 'create';
    if (isAddressValid(validate())) {
      setLoading(true);
      api.zonePriceTable[`${method}Table`]({ fields: record }, record.id).then(() => {
        onClose();
        setLoading(false);
        update({});
      });
    }
  };

  return (
    <Dialog
      className="dialog-md"
      open={open}
      onClose={onClose}
      aria-labelledby="preview-modal-title"
    >
      <DialogTitle id="preview-modal-title">
        {`${isEdit ? 'Edit' : 'Add New'} Record`}
        <IconButton onClick={onClose}>
          <Clear />
        </IconButton>
      </DialogTitle>
      <DialogContent className="preview-item-container" id="preview-modal-dialog">
        <div className="modal-body">
          <div className="row">
            <div className="col-sm-6 form-group">
              <label htmlFor="zone">
                Zone
                <span className="required">*</span>
              </label>
              <input
                className="form-control"
                id="zone"
                value={record.zone || ''}
                onChange={e => handleChange(e)}
              />
              {errors.zone && (
                <label className="error">
                  {errors.zone}
                </label>
              )}
            </div>
            <div className="col-sm-6 form-group">
              <label htmlFor="price">
                Price
                <span className="required">*</span>
              </label>
              <input
                className="form-control"
                id="price"
                value={record.price || ''}
                onChange={e => handleChange(e)}
              />
              {errors.price && (
                <label className="error">
                  {errors.price}
                </label>
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6 form-group">
              <label htmlFor="weight">
                Weight
                <span className="required">*</span>
              </label>
              <input
                className="form-control"
                id="weight"
                value={record.weight || ''}
                onChange={e => handleChange(e)}
              />
              {errors.weight && (
                <label className="error">
                  {errors.weight}
                </label>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-sm-6 form-group">
              <label htmlFor="company_id">
                Company
                <span className="required">*</span>
              </label>
              <select
                className="form-control"
                id="company_id"
                value={record.company_id || ''}
                onChange={e => handleChange(e)}
              >
                <option key="default" value="">Please Select</option>
                {companies.map(item => <option key={item[1]} value={item[1]}>{item[0]}</option>)}
              </select>
              {errors.company_id && (
                <label className="error">
                  {errors.company_id}
                </label>
              )}
            </div>
            <div className="col-sm-6 form-group">
              <label htmlFor="carrier_id">
                Carrier
                <span className="required">*</span>
              </label>
              <select
                className="form-control"
                id="carrier_id"
                value={record.carrier_id || ''}
                onChange={e => handleChange(e)}
              >
                <option key="default" value="">Please Select</option>
                {carriers?.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              {errors.carrier_id && (
                <label className="error">
                  {errors.carrier_id}
                </label>
              )}
            </div>
            <div className="col-sm-6 form-group">
              <label htmlFor="carrier_service_id">
                Carrier Service
                <span className="required">*</span>
              </label>
              <select
                className="form-control"
                id="carrier_service_id"
                value={record.carrier_service_id || ''}
                onChange={e => handleChange(e)}
              >
                <option key="default" value="">Please Select</option>
                {carrierServices.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              {errors.carrier_service_id && (
                <label className="error">
                  {errors.carrier_service_id}
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="modal-bottom-btn text-right">
          <button disabled={loading} className="btn bg-olive" onClick={handleSubmit}>{`${isEdit ? 'Update' : 'Add'} Record`}</button>
          <button className="btn btn-primary" style={{ marginLeft: '5px' }} onClick={onClose}>Cancel</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

SetZonePriceTable.propTypes = {
  row: PropTypes.instanceOf(Object).isRequired,
  companies: PropTypes.instanceOf(Array).isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool,
  update: PropTypes.func.isRequired,
};

SetZonePriceTable.defaultProps = {
  open: false,
};

export default SetZonePriceTable;
